import Body from "../components/Wrapper/Body";
import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import classes from "../Styles/ArticleStyles.module.css";
import { blogs } from "../Data/blogs";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";

const SITE_URL = "https://sprinterwave.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og/blog-cover.jpg`;

const toAbsoluteUrl = (path) => {
  if (!path) return DEFAULT_OG_IMAGE;
  return path.startsWith("http") ? path : `${SITE_URL}${path}`;
};

// Turns markdown into a short, clean plain-text excerpt for meta/description
// and structured-data use, since we don't want to expose raw markdown syntax
// (##, **, links, etc.) to search engines or social previews.
const markdownToExcerpt = (markdown, maxLength = 155) => {
  const plain = markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "") // images
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1") // links -> link text
    .replace(/[#>*_`~-]/g, "") // markdown symbols
    .replace(/\s+/g, " ")
    .trim();

  if (plain.length <= maxLength) return plain;
  return plain.slice(0, maxLength).replace(/\s+\S*$/, "") + "…";
};



function ArticlePage() {
  const location = useLocation();
  const [content, setContent] = useState("");
  const [articleData, setArticleData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const page = useMemo(
    () => new URLSearchParams(location.search).get("page") || "",
    [location.search]
  );

  useEffect(() => {
    const match = blogs.find((item) => item.page === page);

    if (!match) {
      setNotFound(true);
      setArticleData(null);
      return;
    }

    setNotFound(false);
    setArticleData({
      page: match.page,
      date: match.date,
      image: match.image,
      title: match.title,
      readTime: match.readTime,
    });

    fetch(`/MD/${page}.md`)
      .then((res) => res.text())
      .then((text) => setContent(text))
      .catch((error) => console.log(error));
  }, [page]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  const canonicalUrl = `${SITE_URL}/article?page=${page}`;
  const excerpt = content
    ? markdownToExcerpt(content)
    : articleData?.title || "";

  if (notFound) {
    return (
      <Body>
        <Helmet htmlAttributes={{ lang: "en" }}>
          <title>Article Not Found | Sprinterwave Blog</title>
          <meta name="robots" content="noindex, follow" />
          <link rel="canonical" href={`${SITE_URL}/blog`} />
        </Helmet>
        <div className={classes.container}>
          <div className={classes.head}>
            <h1>We couldn't find that article</h1>
            <p>
              It may have been moved or removed. Head back to{" "}
              <Link to="/blog">the blog</Link> to find more articles.
            </p>
          </div>
        </div>
      </Body>
    );
  }

  const pageTitle = articleData
    ? `${articleData.title} | Sprinterwave Blog`
    : "Sprinterwave Blog";

  const articleSchema = articleData
    ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: articleData.title,
        description: excerpt,
        image: toAbsoluteUrl(articleData.image),
        datePublished: articleData.date,
        dateModified: articleData.date,
        timeRequired: `PT${articleData.readTime || 1}M`,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": canonicalUrl,
        },
        author: {
          "@type": "Organization",
          name: "Sprinterwave",
          url: SITE_URL,
        },
        publisher: {
          "@type": "Organization",
          name: "Sprinterwave",
          logo: {
            "@type": "ImageObject",
            url: `${SITE_URL}/images/logo.png`,
          },
        },
      }
    : null;

  const breadcrumbSchema = articleData
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
          {
            "@type": "ListItem",
            position: 2,
            name: "Blog",
            item: `${SITE_URL}/blog`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: articleData.title,
            item: canonicalUrl,
          },
        ],
      }
    : null;

  return (
    <Body>
      <Helmet htmlAttributes={{ lang: "en" }}>
        <meta charSet="utf-8" />

        <title>{pageTitle}</title>
        <meta name="description" content={excerpt} />
        <meta name="robots" content="index, follow" />
        <meta
          name="keywords"
          content="Sprinterwave blog, bill payment tips, financial management, mobile payments, logistics"
        />

        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Sprinterwave" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={excerpt} />
        <meta property="og:url" content={canonicalUrl} />
        <meta
          property="og:image"
          content={toAbsoluteUrl(articleData?.image)}
        />
        {articleData?.date && (
          <meta property="article:published_time" content={articleData.date} />
        )}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={excerpt} />
        <meta
          name="twitter:image"
          content={toAbsoluteUrl(articleData?.image)}
        />

        {articleSchema && (
          <script type="application/ld+json">
            {JSON.stringify(articleSchema)}
          </script>
        )}
        {breadcrumbSchema && (
          <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
          </script>
        )}
      </Helmet>

      <div className={classes.container}>
        <div className={classes.head}>
          <button
            onClick={() => window.history.back()}
            aria-label="Go back to the previous page"
          >
            ← Back
          </button>
          <h1>{articleData?.title}</h1>
          <div className={classes.row}>
            <img
              src="/sprinter.png"
              alt="Sprinterwave"
              width={30}
              height={30}
            />
            <p>
              Sprinterwave ·{" "}
              <time dateTime={articleData?.date}>{articleData?.date}</time> ·{" "}
              {articleData?.readTime} min read
            </p>
          </div>
        </div>

        <div className={classes.cover_img}>
          <img
            src={articleData?.image}
            alt={articleData?.title || "Article cover image"}
            loading="eager"
          />
        </div>

        <article className={classes.article_body}>
          {content ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {content}
            </ReactMarkdown>
          ) : (
            <p>Loading article…</p>
          )}
        </article>
      </div>
    </Body>
  );
}

export default ArticlePage;